import { Component, ViewEncapsulation } from '@angular/core';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import AppValues from 'src/app/common/app.values';
import { SearchService } from 'src/app/components/search/search.service';
import { SearchFormTypes } from 'src/app/forms/forms.enum';
import { MaybeUndefined } from 'src/app/utils';
import { getUniqueArray } from '../../../../common/app.helpers';
import { OrganizationInfo } from '../../../../interfaces/profile.interface';
import { OrganizationLink } from '../../../../swagger-api';
import { SelectorOptionsService } from '../selector-options.service';
import { SuggestionSearchFieldShared } from '../suggestion-search-field.shared';
import { MultipleComplexItemsRequiredValidator } from './Validators/multiple-complex-items-required.validator';
import { MultipleComplexTextInputMaxLengthValidator } from './Validators/multiple-complex-text-input-max-length.validator';
import { SelectItem } from './interfaces/interfaces';
import { MultipleComplexTextInputWhitespaceOnlyValidator } from './Validators/multiple-complex-text-input-whitespace-only.validator';
import { MultipleComplexValidator } from './Validators/multiple-complex.validator';

@Component({
  selector: 'app-multiple-complex-search-form-field',
  templateUrl: './multiple-complex-search-form-field.component.html',
  styleUrls: ['./multiple-complex-search-form-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [SelectorOptionsService, SearchService],
})
export class MultipleComplexSearchFormFieldComponent extends SuggestionSearchFieldShared {
  public selectedItems: OrganizationLink[] = [];
  public SearchFormTypes: typeof SearchFormTypes = SearchFormTypes;

  private maxLengthValidator?: MultipleComplexTextInputMaxLengthValidator;
  private requiredValidator?: MultipleComplexItemsRequiredValidator;
  private whitespaceOnlyValidator?: MultipleComplexTextInputWhitespaceOnlyValidator;

  override ngOnInit() {
    super.ngOnInit();

    this.setupCustomValidatorsIfNecessary();
  }

  public onSelectItem(event: NgbTypeaheadSelectItemEvent): void {
    const projectForVerification = event.item as SelectItem;
    event.preventDefault();
    if (projectForVerification.title.trim() !== '') {
      const newItem: OrganizationLink = {
        organization_name: projectForVerification.title,
        organization_user_id: projectForVerification.entity_id,
        organization_link_kind: OrganizationLink.OrganizationLinkKindEnum.REGISTERED,
      };
      this.changeSelectedList(newItem);
    }
    this.checkCommonValidationErrors();
  }

  public onCheckAndSelectCustomItem(event: KeyboardEvent & { target: HTMLInputElement }): void {
    event.preventDefault();

    this.checkTextInputMaxLengthError();

    if (event.code === AppValues.KEYCODE_ENTER && event.target.value.trim() !== '') {
      this.onSelectCustomItem(event.target.value);
    }
  }

  public onSelectCustomItem(organizationName: string): void {
    if (organizationName.length > 0 && this.maxLengthValidator.isValid) {
      const newItem: OrganizationLink = {
        organization_name: organizationName,
        organization_link_kind: OrganizationLink.OrganizationLinkKindEnum.CUSTOM,
      };

      this.changeSelectedList(newItem);
      this.checkCommonValidationErrors();
    }
  }

  public onCloseItem(item: OrganizationLink): void {
    this.selectedItems.splice(this.selectedItems.indexOf(item), 1);
    this.multipleSearchInput.nativeElement.focus();

    this.changeSelectedList();
    this.checkCommonValidationErrors();
  }

  public checkErrors(): void {
    // No-op implementation
  }

  public get getErrorMessage(): string {
    if (this.control.errors?.['dynamicMaxLength']) {
      const value = this.multipleSearchInput.nativeElement.value;
      const backendAttribute = this.fieldAttribute.backendAttribute;
      return this.translateService.instant('FIELD_MULTIPLE_SEARCH_MAX_LENGTH_ERROR', {
        currentLength: value.length,
        maxLength: backendAttribute.max_length,
      }) as string;
    } else {
      return this.getValidationError(this.control);
    }
  }

  private checkTextInputMaxLengthError(): void {
    if (this.maxLengthValidator) {
      this.maxLengthValidator.value = this.multipleSearchInput.nativeElement.value;
      this.formValidationService.checkMultipleComplexValidationErrors(this.control, [this.maxLengthValidator]);
    }
  }

  private checkCommonValidationErrors(): void {
    const validators = [this.requiredValidator, this.whitespaceOnlyValidator].filter(
      Boolean,
    ) as MultipleComplexValidator[];

    this.formValidationService.checkMultipleComplexValidationErrors(this.control, validators);
  }

  protected getSelectedItems(): OrganizationLink[] {
    return this.selectedItems;
  }

  protected onControlDisabled(): void {
    if (this.multipleSearchInput) {
      this.multipleSearchInput.nativeElement.value = '';
    }
  }

  protected override onNullControlValue(): void {
    this.selectedItems = [];
    super.onNullControlValue();
  }

  protected initSelectedItems(): void {
    this.selectedItems = (this.control.value as OrganizationLink[]) || [];
    this.fillControlValue();
    this.subscribeToControlChanges();
    this.optionsService.getUpdatedOptions(this.fieldAttribute, this.initialSearchParams, this.selectedItems);
  }

  private fillControlValue(): void {
    this.control.setValue(this.selectedItems);

    if (this.requiredValidator) {
      this.requiredValidator.items = this.selectedItems;
    }

    if (this.whitespaceOnlyValidator) {
      this.whitespaceOnlyValidator.items = this.selectedItems;
    }
  }

  private changeSelectedList(newItem?: OrganizationLink): void {
    if (newItem && this.isNameLinkRegistered(newItem, this.selectedItems)) {
      this.multipleSearchInput.nativeElement.value = '';
      this.fillControlValue();
      return;
    }

    if (newItem && this.isCustomOrganization(newItem) && this.isNameFromList(newItem.organization_name)) {
      this.transformCustomOrganization(newItem);
      this.selectedItems.push(newItem);
      this.multipleSearchInput.nativeElement.value = '';
      return;
    }

    if (newItem) {
      this.selectedItems.push(newItem);
    }

    this.selectedItems = getUniqueArray({
      array: this.selectedItems,
      distinctKey: 'organization_name',
    });

    this.multipleSearchInput.nativeElement.value = '';
    this.fillControlValue();
  }

  private isNameLinkRegistered(newItem: OrganizationLink, selectedItems: OrganizationLink[]): boolean {
    return (
      this.isCustomOrganization(newItem) &&
      selectedItems.map((x: OrganizationLink) => x.organization_name).includes(newItem.organization_name)
    );
  }

  private isCustomOrganization(newItem: OrganizationLink): boolean {
    return newItem.organization_link_kind === OrganizationLink.OrganizationLinkKindEnum.CUSTOM;
  }

  private isNameFromList(name: string): boolean {
    return this.optionsService.allItems.map((organization: OrganizationInfo) => organization.name).includes(name);
  }

  private transformCustomOrganization(newItem: OrganizationLink): void {
    const searchOrganization: MaybeUndefined<OrganizationInfo> = this.optionsService.allItems.find(
      (organization: OrganizationInfo) => organization.name === newItem.organization_name,
    );

    if (searchOrganization) {
      newItem.organization_user_id = searchOrganization.userId;
      newItem.organization_link_kind = OrganizationLink.OrganizationLinkKindEnum.REGISTERED;
    }
  }

  private setupCustomValidatorsIfNecessary(): void {
    const attribute = this.fieldAttribute.backendAttribute;

    if (attribute.max_length) {
      this.maxLengthValidator = new MultipleComplexTextInputMaxLengthValidator(
        this.fieldAttribute.backendAttribute.max_length,
      );
    }

    if (attribute.required) {
      this.requiredValidator = new MultipleComplexItemsRequiredValidator();
    }

    if (attribute.name === 'linked_organizations' || attribute.name === 'organization_which_represented') {
      this.whitespaceOnlyValidator = new MultipleComplexTextInputWhitespaceOnlyValidator('organizationName');
    }
  }
}
